import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../../components/ui/button";
import { ArrowRight, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientTeamPage() {
  const [locations, setLocations] = useState([]);
  const [selectedLocId, setSelectedLocId] = useState(null);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const locRes = await axios.get(`${API}/public/locations`);
        const locs = locRes.data || [];
        setLocations(locs);
        if (locs.length) setSelectedLocId(locs[0].location_id);
      } catch (err) {
        console.error("Failed to load public locations:", err);
      }
      setLoading(false);
    })();
  }, []);

  useEffect(() => {
    if (!selectedLocId) return;
    axios.get(`${API}/public/providers?location_id=${selectedLocId}`).then(res => setTeam(res.data || []));
  }, [selectedLocId]);

  return (
    <div data-testid="client-team-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-violet-50 via-white to-rose-50/40 border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <p className="text-xs font-bold uppercase tracking-widest text-violet-500 font-['Manrope'] mb-3">Our Team</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 mb-3" data-testid="team-page-title">
            Expert hands,<br />unforgettable results.
          </h1>
          <p className="text-lg text-slate-600 font-['Manrope'] max-w-2xl">
            Every member of our team is carefully selected, certified, and continuously trained in the latest techniques. Meet the people who'll help you look and feel amazing.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        {locations.length > 1 && (
          <div className="flex items-center gap-2 flex-wrap mb-8" data-testid="team-location-filter">
            {locations.map(l => (
              <button key={l.location_id} onClick={() => setSelectedLocId(l.location_id)}
                data-testid={`team-loc-filter-${l.location_id}`}
                className={`px-4 py-2 rounded-full text-sm font-medium font-['Manrope'] transition-all ${
                  selectedLocId === l.location_id ? "bg-slate-900 text-white" : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                }`}>
                {l.name}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-sm text-slate-400 font-['Manrope']">Loading…</div>
        ) : team.length === 0 ? (
          <div className="py-20 text-center text-sm text-slate-400 font-['Manrope']">No team members found at this location.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {team.map(t => (
              <div key={t.user_id} className="group bg-white border border-slate-100 rounded-3xl p-6 hover:shadow-lg transition-all" data-testid={`team-card-${t.user_id}`}>
                <div className="relative mb-5">
                  <div className="aspect-square rounded-2xl overflow-hidden flex items-center justify-center text-white text-5xl font-semibold font-['Outfit']"
                    style={{ background: `linear-gradient(135deg, ${t.color || "#B5A493"}, ${t.color || "#B5A493"}aa)` }}>
                    {t.name?.[0]}
                  </div>
                  <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-xl bg-white shadow-md flex items-center justify-center border border-slate-100">
                    <Sparkles className="w-4 h-4 text-rose-500" strokeWidth={2} />
                  </div>
                </div>
                <h3 className="font-['Outfit'] text-xl font-semibold text-slate-900 mb-1">{t.name}</h3>
                <p className="text-sm text-slate-500 font-['Manrope'] mb-4">{t.title || "Beauty Professional"}</p>
                {t.specializations && t.specializations.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mb-5">
                    {t.specializations.slice(0, 4).map((s, i) => (
                      <span key={i} className="inline-flex text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full bg-slate-50 text-slate-600 border border-slate-100 font-['Manrope']">
                        {s}
                      </span>
                    ))}
                  </div>
                )}
                <Link to="/salon/book">
                  <Button size="sm" className="w-full h-10 bg-slate-50 hover:bg-slate-900 hover:text-white text-slate-900 rounded-full text-sm font-semibold transition-all border-0" data-testid={`book-with-${t.user_id}`}>
                    Book with {t.name?.split(" ")[0]} <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
